import { TenantSelectionSection } from '@app/layouts/MainLayout/components/OutboundCall/OutboundCallModal/components/OutboundModalKeypad/TenantSelectionSection/TenantSelectionSection';
import CallingDisabled from '@components/CallingDisabledGuard/CallingDisabledGuard';
import { DYNAMIC_CALLER_ID_NUMBER } from '@components/OutboundCallerIdSelection/constants';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import { mainLayoutLocators } from '@locators/main-layout';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { FeatureFlagKeys, useFeatureFlags } from '@modules/FeatureFlagModule';
import usePermissions from '@app/permissions/usePermissions';
import { permissions } from '@app/permissions/permissions';
import useInboxState from '@modules/InboxModule/hooks/useInboxState';
import {
  Button,
  ButtonGroup,
  ButtonToggle,
  Modal,
  Stack,
} from '@servicetitan/design-system';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { getTenantId } from 'src/constants/tenantSelection';
import { BlockedActivityTooltip } from './components/OutboundModalEmployeeTable/BlockedActivityTooltip';
import OutboundModalEmployeeTable from './components/OutboundModalEmployeeTable/OutboundModalEmployeeTable';
import OutboundModalKeypad from './components/OutboundModalKeypad/OutboundModalKeypad';
import { useIsKeypadNumberBlockedByActivity } from './components/OutboundModalKeypad/useIsKeypadNumberBlockedByActivity';
import OutboundModalQueues from './components/OutboundModalQueues';
import OutboundModalTechnicianTable from './components/OutboundModalTechnicianTable/OutboundModalTechnicianTable';
import TransferModalQueues from './components/TransferModalQueues/TransferModalQueues';
import { initialCallDetails, useOutboundCallModalContext } from './context';
import styles from './OutboundCallModal.module.css';
import { OutboundCallsAllowedBanner } from './OutboundCallsAllowedBanner';
import { OutboundCallDetails, TabNameEnum, ITab } from './types';
import {
  InteractionContactInput,
  InteractionContactType,
} from '@api/generated/graphql';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import { shouldUseSoftPhone } from '@lib/utils/shouldUseSoftPhone';
import { verifyAgentStatus } from '@modules/ServiceModule/workspace/lib/verifyAgentStatus';
import TasksModule from '@modules/TasksModule';
import { TaskState } from '@modules/TasksModule/types';
import TechniciansModule, {
  TechnicianTableRow,
} from '@modules/TechniciansModule';

export type OutboundCallModalMode = 'outbound' | 'transfer';

const OFFICE_TAB_LABEL = 'Office';

export interface OutboundCallModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title?: string;
  actionButton?: {
    text: string;
    onClick: (data: OutboundCallDetails) => void;
    disabled?: boolean;
  };
  tabs?: ITab[];
  onClose?: () => void;
  animate?: boolean;
  id?: string;
  showTenantSelection?: boolean;
  mode?: OutboundCallModalMode;
  onTechnicianNext?: (details: OutboundCallDetails) => void;
}

const initialTabs: ITab[] = [
  { value: TabNameEnum.Employees, selected: true },
  { value: TabNameEnum.Technicians, hidden: true },
  { value: TabNameEnum.Queue },
  { value: TabNameEnum.Keypad },
];

const TabsToUseDID = [TabNameEnum.Employees, TabNameEnum.Queue];

/** Find the correct tab to keep in the component state */
const findSelectedTab = (tabs: ITab[]): TabNameEnum => {
  let selectedTab: ITab | undefined;
  let employeeTab: ITab | undefined;

  for (const tab of tabs) {
    if (tab.selected) {
      selectedTab = tab;
      break;
    }

    if (tab.value === 'Employees' && !tab.disabled) {
      employeeTab = tab;
    }
  }

  if (selectedTab) {
    return selectedTab.value;
  }

  if (employeeTab) {
    return employeeTab.value;
  }

  return TabNameEnum.Keypad;
};

// An outbound call is Pending when placed, Offered while it rings, Accepted once
// answered. WrapUp and later states are ended calls.
const LIVE_OUTBOUND_STATES = [
  TaskState.Pending,
  TaskState.Offered,
  TaskState.Accepted,
];

function attachTechnicianContactByPhone(
  phone: string,
  technician: TechnicianTableRow | undefined,
): void {
  if (!technician) return;
  // The new call is the newest live outbound task to this number. An ended
  // call to the same number may still be open in WrapUp (retained card) and
  // must never receive the contact, nor an interaction write for it.
  const task = TasksModule.getTasks()
    .filter(
      (t) =>
        t.direction === 'outbound' &&
        LIVE_OUTBOUND_STATES.includes(t.state) &&
        sanitizePhoneNumberTo10Digits(t.from) ===
          sanitizePhoneNumberTo10Digits(phone),
    )
    .sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())[0];
  if (task) {
    TasksModule.setContactsOnTask(task.id, [
      {
        type: InteractionContactType.Technician,
        id: technician.id,
        name: technician.name,
      },
    ]);
  }
}

/**
 * Returns a modal component that allow users to call employees, queues, or an external number.
 *
 * The modal consists of 3 "tabs": Employees, Queue, and Keypad, and each of them allows the user
 * to make a call in a different way.
 * - Employees: displays a list of employees that the user can call
 * - Queue: displays a list of queues that the user can call
 * - Keypad: the user can dial in a number and call it
 *
 * @param props: OutboundCallModalProps
 * @returns
 */
const OutboundCallModal: FC<OutboundCallModalProps> = ({
  open,
  setOpen,
  title = 'Make a Call',
  tabs = initialTabs,
  actionButton,
  onClose,
  animate = true,
  id,
  showTenantSelection = false,
  mode = 'outbound',
  onTechnicianNext,
}) => {
  const {
    callToDetails,
    callFromDetails,
    setCallToDetails,
    callButtonEnabled,
    outboundCallsAllowed,
    outboundCall,
    isProcessingCall,
  } = useOutboundCallModalContext();
  const {
    [FeatureFlagKeys.featureCallTransferToTechnicians]:
      featureCallTransferToTechnicians,
    [FeatureFlagKeys.featureDisplayTechnicianConversationHistory]:
      featureDisplayTechnicianConversationHistory,
  } = useFeatureFlags();

  const hasTransferToTechniciansPermission = usePermissions(
    [permissions.general.ACTION_CALL_OPTIONS.CALL_TRANSFER_TO_TECHNICIANS.VIEW],
    'all',
  );

  const showTechniciansTab =
    featureCallTransferToTechnicians && hasTransferToTechniciansPermission;

  const { user } = useAuth();
  const agentPersonalLine = user?.personalLine?.phoneNumber;
  const inboxState = useInboxState();
  const selectedTenantId = getTenantId(
    inboxState.selectedTenantForOutboundCall?.id ?? null,
  );

  const [selectedTab, setSelectedTab] = useState<TabNameEnum>(
    findSelectedTab(tabs),
  );

  // Gate keypad calls/transfers to busy known users when the agent
  // lacks the active-employees permission. Only applies to the Keypad tab —
  // other tabs have their own gating (Office tab handles employees directly).
  const isKeypadNumberBlocked = useIsKeypadNumberBlockedByActivity(
    callToDetails.number,
  );
  const keypadBlockedByActivity =
    isKeypadNumberBlocked && selectedTab === TabNameEnum.Keypad;

  const handleClose = useCallback(() => {
    setCallToDetails(initialCallDetails);
    setSelectedTab(TabNameEnum.Employees);

    setOpen(false);
  }, [setCallToDetails, setOpen]);

  const handleTabChange = (val: TabNameEnum) => {
    setCallToDetails(initialCallDetails);
    setSelectedTab(val);
  };

  const handleSetNumber = (number: string) => {
    setCallToDetails({ ...callToDetails, number });
  };

  const handlePrimaryButtonClick = () => {
    primaryButton.onClick();
    setCallToDetails((prev) => ({
      ...prev,
      entity: OutboundCallEntity[selectedTab],
    }));
  };

  const handleCall = useCallback(async () => {
    if (selectedTab === TabNameEnum.Employees && callToDetails.agentId) {
      void verifyAgentStatus(callToDetails.agentId);
    }

    let callerNumber =
      callFromDetails.number === DYNAMIC_CALLER_ID_NUMBER
        ? null
        : callFromDetails.number;
    if (agentPersonalLine && TabsToUseDID.includes(selectedTab)) {
      callerNumber = agentPersonalLine;
    }

    let tenantId: string | undefined;
    if (selectedTab === TabNameEnum.Technicians) {
      tenantId = callToDetails.tenantId;
    } else if (selectedTab === TabNameEnum.Keypad) {
      tenantId = selectedTenantId ?? undefined;
    }

    let contacts: InteractionContactInput[] | undefined;

    if (featureDisplayTechnicianConversationHistory) {
      if (
        selectedTab === TabNameEnum.Technicians &&
        callToDetails.technicianId
      ) {
        contacts = [
          {
            type: InteractionContactType.Technician,
            id: callToDetails.technicianId,
            name: callToDetails.callDisplayName,
          },
        ];
      } else if (selectedTab === TabNameEnum.Keypad) {
        // Sync when cache is loaded, Promise when still fetching from BFF.
        const technicianResult = TechniciansModule.findTechnicianByPhone(
          callToDetails.number,
        );

        if (technicianResult instanceof Promise) {
          // Cache not yet loaded — start the call immediately without contacts
          // so the user isn't blocked, then attach the technician contact once
          // the lookup resolves. InteractionsModule will pick it up on the next
          // task.updated(state) event when the task reaches a recordable state.
          await outboundCall({
            phoneNumber: callToDetails.number,
            customerName: callToDetails.callDisplayName,
            callerNumber,
            tenantId,
          });
          handleClose();
          const phone = callToDetails.number;
          void technicianResult.then((technician) =>
            attachTechnicianContactByPhone(phone, technician),
          );
          return;
        }

        // Cache was loaded — technician result is available synchronously.
        // Pass contacts directly so they are included in the initial upsert.
        if (technicianResult) {
          contacts = [
            {
              type: InteractionContactType.Technician,
              id: technicianResult.id,
              name: technicianResult.name,
            },
          ];
        }
      }
    }

    await outboundCall({
      phoneNumber: callToDetails.number,
      customerName: callToDetails.callDisplayName,
      callerNumber,
      tenantId,
      contacts,
    });
    handleClose();
  }, [
    callFromDetails.number,
    agentPersonalLine,
    selectedTab,
    callToDetails,
    selectedTenantId,
    outboundCall,
    handleClose,
    featureDisplayTechnicianConversationHistory,
  ]);

  const primaryButton = useMemo(() => {
    const isTechnicianNextStep =
      !!featureCallTransferToTechnicians &&
      selectedTab === TabNameEnum.Technicians &&
      !!onTechnicianNext;

    const onClick = () => {
      if (isTechnicianNextStep) {
        onTechnicianNext?.(callToDetails);
        handleClose();
      } else if (actionButton?.onClick) {
        actionButton.onClick(callToDetails);
      } else {
        handleCall();
      }
    };

    const hasTenantRequirementMet =
      !showTenantSelection ||
      !(selectedTab === TabNameEnum.Keypad) || // if not in keypad, no need to check for tenant
      // otherwise check for tenant selection
      (inboxState.selectedTenantForOutboundCall && user?.org.tenants.length);

    const isDisabled =
      actionButton?.disabled || // If the action button is explicitly disabled
      !callButtonEnabled || // If the call button is not enabled (e.g., missing required data)
      (!hasTenantRequirementMet && !shouldUseSoftPhone(callToDetails.number)) || // either tenant requirement met or it should call with softphone
      callFromDetails.loading || // If tenant change and from number is calculating
      keypadBlockedByActivity; // Keypad DID resolves to a busy known user

    return {
      text: isTechnicianNextStep ? 'Next' : actionButton?.text ?? 'Call',
      onClick: onClick,
      disabled: isDisabled,
    };
  }, [
    actionButton,
    callButtonEnabled,
    callToDetails,
    callFromDetails.loading,
    handleClose,
    inboxState.selectedTenantForOutboundCall,
    user?.org.tenants,
    handleCall,
    selectedTab,
    featureCallTransferToTechnicians,
    showTenantSelection,
    onTechnicianNext,
    keypadBlockedByActivity,
  ]);

  const tabOptions = useMemo(() => {
    return tabs
      .filter(
        (tab) => tab.value !== TabNameEnum.Technicians || showTechniciansTab,
      )
      .map((tab) => ({
        text:
          showTechniciansTab && tab.value === TabNameEnum.Employees
            ? OFFICE_TAB_LABEL
            : tab.value,
        value: tab.value,
        selected: selectedTab === tab.value,
        disabled: tab.disabled,
      }));
  }, [tabs, selectedTab, showTechniciansTab]);

  return (
    <Modal
      className={styles.outboundCallModal}
      open={open}
      focusTrapOptions={{ disabled: true }}
      portal={true}
      title={title}
      onClose={onClose}
      animate={animate}
      id={id}
      footer={
        <ButtonGroup>
          <Button
            onClick={handleClose}
            id={mainLayoutLocators.outboundAndTransferModal.secondaryActionBtn}
            disabled={isProcessingCall}
          >
            Cancel
          </Button>
          <BlockedActivityTooltip blocked={keypadBlockedByActivity}>
            <CallingDisabled additionalDisabled={primaryButton.disabled}>
              <Button
                primary
                id={
                  mainLayoutLocators.outboundAndTransferModal.primaryActionBtn
                }
                onClick={handlePrimaryButtonClick}
                loading={isProcessingCall}
                disabled={primaryButton.disabled || isProcessingCall}
              >
                {primaryButton.text}
              </Button>
            </CallingDisabled>
          </BlockedActivityTooltip>
        </ButtonGroup>
      }
    >
      <Stack direction='column' spacing={2}>
        <ButtonToggle
          className={styles.outboundCallModalTabs}
          onChange={handleTabChange}
          options={tabOptions}
          data-testid={'call-modal-tabs'}
        />

        {tabOptions.map((tab) => {
          if (!tab.selected) {
            return null;
          }
          switch (tab.value) {
            case TabNameEnum.Employees:
              return (
                <OutboundModalEmployeeTable
                  setCallDetails={setCallToDetails}
                  key={tab.value}
                />
              );
            case TabNameEnum.Queue:
              return mode === 'transfer' ? (
                <TransferModalQueues
                  setCallDetails={setCallToDetails}
                  key={tab.value}
                />
              ) : (
                <OutboundModalQueues
                  setCallDetails={setCallToDetails}
                  key={tab.value}
                />
              );
            case TabNameEnum.Keypad:
              return (
                <Fragment key={tab.value}>
                  {!outboundCallsAllowed && <OutboundCallsAllowedBanner />}

                  {showTenantSelection && <TenantSelectionSection />}

                  <OutboundModalKeypad
                    number={callToDetails.number}
                    setNumber={handleSetNumber}
                  />
                  <div className={styles.keypadFooterSpace} />
                </Fragment>
              );
            case TabNameEnum.Technicians:
              return (
                <OutboundModalTechnicianTable
                  setCallDetails={setCallToDetails}
                  key={tab.value}
                />
              );
            default:
              return null;
          }
        })}
      </Stack>
    </Modal>
  );
};

export default OutboundCallModal;
